import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { PhoneFrame } from './phone/PhoneFrame';
import { DiscoverScreen } from './screens/DiscoverScreen';
import { CheckInScreen } from './screens/CheckInScreen';
import { EventScreen } from './screens/EventScreen';
import { PlayScreen } from './screens/PlayScreen';
import { ConsentScreen } from './screens/ConsentScreen';
import { DashboardScreen } from './screens/DashboardScreen';
import { tourSteps, TourScreen } from '../data/tourSteps';
const ease = [0.23, 1, 0.32, 1] as const;
const STEP_MS = 6000;
const screens: Record<TourScreen, () => JSX.Element> = {
  discover: DiscoverScreen,
  checkin: CheckInScreen,
  event: EventScreen,
  play: PlayScreen,
  consent: ConsentScreen,
  dashboard: DashboardScreen
};
export function AppTour() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, {
    amount: 0.4
  });
  const reduce = useReducedMotion();
  const running = inView && !paused && !reduce;
  useEffect(() => {
    if (!running) return;
    const t = window.setTimeout(() => setActive(a => (a + 1) % tourSteps.length), STEP_MS);
    return () => window.clearTimeout(t);
  }, [active, running]);
  const step = tourSteps[active];
  const ActiveScreen = screens[step.screen];
  return <section ref={ref} id="app" className="pb-20 lg:pb-24" aria-labelledby="tour-title" data-id="element-13" data-mp="3vzbfyz76hi3r">
      <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-14" data-mp="3qzyhfh6y2mlc">
        <div className="leaf-xl grid overflow-hidden bg-white ring-1 ring-line lg:grid-cols-12" data-id="element-15" data-mp="3r0qbgnkw8c9i">
          <div className="p-7 sm:p-10 lg:col-span-6 lg:p-14" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-16" data-mp="3m26ocjukcufk">
            <h2 id="tour-title" className="font-display text-4xl font-bold leading-[1.05] tracking-[-0.03em] text-ink sm:text-5xl" data-id="element-17" data-mp="3d18kc1k4kmwz">
              Your visit, step by step.
            </h2>
            <p className="mt-4 max-w-md text-lg leading-relaxed text-muted" data-id="element-18" data-mp="37fgh71u5s7m2">
              From the first scan at the venue to the prize in your pocket.
            </p>

            <ol className="mt-8 divide-y divide-line border-y border-line" data-id="element-19" data-mp="2yei7c2x1h5vk">
              {tourSteps.map((s, i) => {
              const isActive = i === active;
              return <li key={s.screen} data-id="element-20" data-mp="3q3ldxpceesbd" data-mpi={i}>
                    <button type="button" onClick={() => setActive(i)} aria-current={isActive ? 'step' : undefined} className="group w-full py-4 text-left focus-visible:outline-none" data-id="element-21" data-mp="3q6r3t6supy85">
                      <span className="flex items-center gap-4" data-id="element-22" data-mp="8v87qzjr6mlm">
                        <span className={`w-6 font-display text-sm font-bold transition-colors duration-200 ${isActive ? 'text-forest' : 'text-muted/50'}`} data-id="element-23" data-mp="dob239wcu02v">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className={`font-display text-lg font-bold tracking-tight transition-colors duration-200 group-focus-visible:underline ${isActive ? 'text-ink' : 'text-muted group-hover:text-ink'}`} data-id="element-24" data-mp="wtpb1qiqi4ah">
                          {s.title}
                        </span>
                      </span>
                      <AnimatePresence initial={false} data-id="element-25" data-mp="92jwrfsfzk2a">
                        {isActive && <motion.span initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.25,
                      ease
                    }} className="block overflow-hidden" data-id="element-26" data-mp="1pga18nnh4acg">
                            <span className="block pl-10 pt-2 leading-relaxed text-muted" data-id="element-27" data-mp="3yj2d9643wxv">{s.description}</span>
                            <span className="ml-10 mt-4 block h-0.5 overflow-hidden rounded-full bg-line" data-id="element-28" data-mp="7zuy7wfkxeei">
                              <motion.span key={`${active}-${running}`} className="block h-full origin-left rounded-full bg-forest" initial={{
                          scaleX: 0
                        }} animate={{
                          scaleX: running ? 1 : 0
                        }} transition={running ? {
                          duration: STEP_MS / 1000,
                          ease: 'linear'
                        } : {
                          duration: 0
                        }} data-id="element-29" data-mp="czpopyvg01z1" />
                            </span>
                          </motion.span>}
                      </AnimatePresence>
                    </button>
                  </li>;
            })}
            </ol>
          </div>

          <div className="relative flex items-center justify-center overflow-hidden bg-mint-soft px-6 py-12 lg:col-span-6" data-id="element-30" data-mp="2g6ye3npitgbv">
            <div className="leaf-lg absolute inset-x-10 inset-y-24 bg-mint/40" aria-hidden="true" data-id="element-31" data-mp="2c6egyk4y6cpq" />
            <div className="relative" data-id="element-32" data-mp="278w4wkvmvhdi">
              <PhoneFrame label={`App screen: ${step.label}`} data-id="element-33" data-mp="33x2e7l3i2xy4">
                <AnimatePresence initial={false} data-id="element-34" data-mp="33zevdw1fp4a4">
                  <motion.div key={active} initial={{
                  opacity: 0,
                  y: 16,
                  scale: 0.98
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -16,
                  scale: 0.98
                }} transition={{
                  duration: 0.28,
                  ease
                }} className="absolute inset-0" data-id="element-35" data-mp="2zvgxtprrc1j6">
                    <ActiveScreen data-id="element-36" data-mp="3iuuuifgvfqdd" />
                  </motion.div>
                </AnimatePresence>
              </PhoneFrame>

              <AnimatePresence mode="wait" initial={false} data-id="element-37" data-mp="5mgz0hmj7kxf">
                <motion.div key={active} initial={{
                opacity: 0,
                scale: 0.96
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.96
              }} transition={{
                duration: 0.2,
                ease
              }} className="absolute -left-4 bottom-20 hidden max-w-[220px] rounded-2xl bg-white p-3.5 shadow-xl sm:block xl:-left-20" data-id="element-38" data-mp="akis7vdo90mm">
                  <p className="font-display text-xs font-bold text-sage" data-id="element-39" data-mp="3nzoussr1vf57">{step.label}</p>
                  <ul className="mt-2 space-y-1.5" data-id="element-40" data-mp="3a05s4fh0cw0a">
                    {step.details.map(d => <li key={d} className="flex items-center gap-2 text-sm font-medium text-ink" data-id="element-41" data-mp="3e0h83eyfe85f">
                        <CheckIcon className="h-3.5 w-3.5 shrink-0 text-forest" strokeWidth={2.5} data-id="element-42" data-mp="1881073zidypk" />
                        {d}
                      </li>)}
                  </ul>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}